import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  UserCog,
  Plus,
  Edit,
  Trash2,
  Shield,
  Building2,
  Mail,
  User,
  Eye,
  EyeOff,
  X,
  AlertCircle,
} from "lucide-react";
import toast from "react-hot-toast";
import { isEstateSuperAdmin, isPlatformSuperAdmin } from "../utils/permissions";
import { useAuth } from "../contexts/AuthContext";
import { adminApi, estateApi } from "../services/api";

const AdminManagement = () => {
  const { admin } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("create");
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    name: "",
    email: "",
    role: "estate_admin",
    estate: "",
  });

  const queryClient = useQueryClient();

  const canManageAdmins =
    isPlatformSuperAdmin(admin) || isEstateSuperAdmin(admin);

  // RESTRICT ACCESS: Only platform or estate super admins can manage admins
  if (!canManageAdmins) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Access Denied
          </h3>
          <p className="text-gray-600">
            Only platform or estate super admins can manage admins.
          </p>
        </div>
      </div>
    );
  }

  // Fetch all admins
  const { data: admins, isLoading } = useQuery(
    "admins",
    async () => {
      const response = await adminApi.get("/auth/admin/all");
      return response.data.data || response.data;
    },
    {
      retry: 1,
    }
  );

  // Fetch estates for assignment
  const { data: estates } = useQuery("estates", async () => {
    const response = await estateApi.getAll();
    return response;
  });

  // Create admin mutation
  const createAdmin = useMutation(
    async (data) => {
      console.log("=== CREATE ADMIN REQUEST ===");
      console.log("Payload:", JSON.stringify(data, null, 2));

      try {
        const response = await adminApi.post("/auth/admin/create", data);
        console.log("=== CREATE ADMIN SUCCESS ===");
        console.log("Response:", response.data);
        return response.data;
      } catch (error) {
        console.log("=== CREATE ADMIN ERROR ===");
        console.log("Error response:", error.response?.data);
        console.log("Error status:", error.response?.status);
        throw error;
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("admins");
        toast.success("Admin created successfully!");
        setShowModal(false);
        resetForm();
      },
      onError: (error) => {
        const message =
          error.response?.data?.message || "Failed to create admin";
        toast.error(message);
      },
    }
  );

  // Delete admin mutation
  const deleteAdmin = useMutation(
    async (adminId) => {
      const response = await adminApi.delete(`/auth/admin/${adminId}`);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("admins");
        toast.success("Admin deleted successfully!");
      },
      onError: (error) => {
        const message =
          error.response?.data?.message || "Failed to delete admin";
        toast.error(message);
      },
    }
  );

  const resetForm = () => {
    setFormData({
      username: "",
      password: "",
      name: "",
      email: "",
      role: "estate_admin",
      estate: "",
    });
    setSelectedAdmin(null);
    setShowPassword(false);
  };

  const handleCreate = () => {
    setModalType("create");
    setShowModal(true);
    resetForm();
  };

  const handleEdit = (admin) => {
    // Edit functionality disabled - endpoint not available
    toast.error(
      "Admin editing is currently unavailable. Please delete and recreate the admin if changes are needed."
    );
  };

  const handleDelete = (admin) => {
    if (
      window.confirm(
        `Are you sure you want to delete admin "${admin.name}"? This action cannot be undone.`
      )
    ) {
      deleteAdmin.mutate(admin._id);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation
    if (!formData.username || !formData.name || !formData.email) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (modalType === "create" && !formData.password) {
      toast.error("Password is required");
      return;
    }

    // Validate estate assignment for Estate Admins
    if (formData.role !== "platform_super_admin") {
      if (!formData.estate) {
        toast.error("Please select an estate for this admin");
        return;
      }
    }

    // Prepare data
    const submitData = {
      username: formData.username,
      name: formData.name,
      email: formData.email,
      role: formData.role,
    };

    // Add password only if provided
    if (formData.password) {
      submitData.password = formData.password;
    }

    // Add estate for estate-scoped admins
    if (formData.role !== "platform_super_admin" && formData.estate) {
      submitData.estateId = formData.estate;
    }

    console.log("=== FINAL SUBMIT DATA ===");
    console.log("Full object:", JSON.stringify(submitData, null, 2));

    // Only create action is available
    createAdmin.mutate(submitData);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "platform_super_admin":
        return "bg-red-100 text-red-800";
      case "estate_super_admin":
        return "bg-purple-100 text-purple-800";
      case "estate_admin":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case "platform_super_admin":
        return "Platform Super Admin";
      case "estate_super_admin":
        return "Estate Super Admin";
      case "estate_admin":
        return "Estate Admin";
      default:
        return role;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Management</h1>
          <p className="text-gray-600">
            Manage administrator accounts and permissions
          </p>
        </div>

        <button
          onClick={handleCreate}
          className="btn btn-primary flex items-center"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Admin
        </button>
      </div>

      {/* Admins Table */}
      <div className="card">
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Admin
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned Estate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Array.isArray(admins) && admins.length > 0 ? (
                  admins.map((admin) => (
                    <tr key={admin._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                            <UserCog className="h-5 w-5 text-white" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {admin.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              @{admin.username}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeColor(
                            admin.role
                          )}`}
                        >
                          {getRoleDisplayName(admin.role)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {admin.role === "platform_super_admin" ? (
                          <span className="text-sm text-gray-500 flex items-center">
                            <Shield className="h-4 w-4 mr-1" />
                            All Estates
                          </span>
                        ) : admin.estate ? (
                          <span className="text-sm text-gray-700 flex items-center">
                            <Building2 className="h-4 w-4 mr-1" />
                            {estates?.find((e) => e._id === admin.estate)
                              ?.name || admin.estate}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">
                            No estate assigned
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-500">
                          <Mail className="h-4 w-4 mr-2" />
                          {admin.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleEdit(admin)}
                            className="text-gray-400 hover:text-gray-600 cursor-not-allowed"
                            title="Edit unavailable - endpoint not implemented"
                            disabled
                          >
                            <Edit className="h-4 w-4" />
                          </button>

                          {admin.role !== "platform_super_admin" && (
                            <button
                              className="text-gray-400 cursor-not-allowed"
                              title="Delete unavailable - endpoint not implemented"
                              disabled
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-6 py-12 text-center text-gray-500"
                    >
                      <UserCog className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                      <p>No admins found</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Create Admin
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                </div>

                <div>
                  <label className="form-label">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">
                    Username <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                </div>

                <div>
                  <label className="form-label">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="form-input pr-10"
                      required
                      minLength="6"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Minimum 6 characters
                  </p>
                </div>
              </div>

              {/* Role Selection */}
              <div>
                <label className="form-label">
                  Role <span className="text-red-500">*</span>
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                >
                  {isPlatformSuperAdmin(admin) && (
                    <option value="platform_super_admin">
                      Platform Super Admin
                    </option>
                  )}
                  {isPlatformSuperAdmin(admin) && (
                    <option value="estate_super_admin">
                      Estate Super Admin
                    </option>
                  )}
                  <option value="estate_admin">Estate Admin</option>
                </select>
              </div>

              {/* Estate Assignment - Only for estate-scoped admins */}
              {formData.role !== "platform_super_admin" && (
                <div>
                  <label className="form-label">
                    Assigned Estate <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="estate"
                    value={formData.estate}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  >
                    <option value="">Select an estate</option>
                    {estates && estates.length > 0 ? (
                      estates.map((estate) => (
                        <option key={estate._id} value={estate._id}>
                          {estate.name}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>
                        No estates available
                      </option>
                    )}
                  </select>
                </div>
              )}

              {/* Form Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createAdmin.isLoading}
                  className="btn btn-primary"
                >
                  {createAdmin.isLoading ? (
                    <>
                      <div className="spinner mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    "Create Admin"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminManagement;
